/*
  Product gallery Swiper engine.

  Drop-in replacement for the Flickity-driven product media gallery.
  Loaded ONLY on pages that render the product media gallery
  (main-product, main-pre-order, gift-card main product). It never runs
  globally, so the rest of the theme keeps using Flickity untouched.

  It reproduces the previous Flickity behaviour 1:1:
    - main slider  + thumbnail slider linked (old `asNavFor` -> Swiper `thumbs`)
    - `wrapAround`  -> `loop`
    - RTL aware     -> `dir="rtl"` on the container
    - mirrors `.is-selected` onto the active main slide so the existing
      zoom-button JS and thumbnail CSS keep working with no changes
    - columns / stacked layouts stay a CSS grid on desktop and only turn
      into a slider on mobile (<750px), exactly like the old mobile-only
      `new Flickity('.product__media-list.contains-media')` did.
*/
(function () {
  'use strict';

  if (typeof Swiper === 'undefined') return;

  var MOBILE_BREAKPOINT = 750;
  var instances = []; // { main, thumbs, teardown, config } for resize handling

  function parseConfig(el, attr) {
    try {
      return JSON.parse(el.getAttribute(attr) || '{}');
    } catch (e) {
      return {};
    }
  }

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  // Keep the legacy `.is-selected` class on the real (non-clone) active slide
  // so existing CSS (`.is-selected ...`) and JS (mobile zoom button) keep working.
  function mirrorSelected(swiper) {
    // swiper.slides is a Dom7 collection in Swiper 8 — copy to a plain array.
    var allSlides = Array.prototype.slice.call(swiper.slides);
    var realSlides = allSlides.filter(function (s) {
      return !s.classList.contains('swiper-slide-duplicate');
    });
    allSlides.forEach(function (s) {
      s.classList.remove('is-selected');
    });
    var active = realSlides[swiper.realIndex];
    if (active) active.classList.add('is-selected');
  }

  // Scroll the active thumbnail into view.
  // Swiper's built-in thumbs auto-scroll is gated on `visibleSlidesIndexes`, which
  // is unreliable for a vertical, JS-sized, slidesPerView:'auto' column (it often
  // reports the active thumb as "visible" and skips scrolling). So we drive it
  // explicitly: refresh the thumbs' visibility bookkeeping, then slideTo the active
  // index if it isn't actually on-screen. Works for vertical AND horizontal.
  function scrollThumbIntoView(mainSwiper) {
    var thumbs = mainSwiper.thumbs && mainSwiper.thumbs.swiper;
    if (!thumbs || thumbs.destroyed) return;
    // main has loop clones, thumbs do not -> map by realIndex.
    var target = mainSwiper.realIndex;
    if (typeof target !== 'number' || target < 0) return;
    // Recompute offsets/visibility so the JS-set height & 'auto' sizes are current.
    if (thumbs.updateSlidesOffset) thumbs.updateSlidesOffset();
    if (thumbs.updateSlidesProgress) thumbs.updateSlidesProgress();
    var visible = thumbs.visibleSlidesIndexes || [];
    if (visible.indexOf(target) !== -1) return; // already on-screen: no jitter
    var maxIndex = thumbs.slides.length - 1;
    if (target > maxIndex) target = maxIndex;
    thumbs.slideTo(target);
  }

  // Create a nav/pagination element and append it to the swiper container.
  function appendControl(container, className) {
    var el = document.createElement('div');
    el.className = className;
    container.appendChild(el);
    return el;
  }

  function countSlides(container) {
    return container.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)').length;
  }

  // Build the Swiper option object shared by main + mobile galleries.
  function buildMainOptions(container, config, thumbsSwiper, slideCount) {
    var loop = !!config.loop && slideCount > 1;
    var options = {
      loop: loop,
      // 'auto' lets the existing CSS drive slide width (e.g. 100% desktop,
      // 80% mobile peek for thumbnail_slider) exactly like Flickity did.
      slidesPerView: 'auto',
      centeredSlides: false,
      spaceBetween: 0,
      // Adapt the slider height to each image — products have unequal aspect
      // ratios (requires image fit = "contain"; "cover" forces a fixed ratio).
      autoHeight: true,
      // Smooth, deliberate slide transition (also the duration the height animates).
      speed: 400,
      rtl: !!config.rtl,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      a11y: { enabled: false },
      on: {
        init: function () { mirrorSelected(this); scrollThumbIntoView(this); },
        slideChange: function () { mirrorSelected(this); scrollThumbIntoView(this); },
        slideChangeTransitionEnd: function () { mirrorSelected(this); }
      }
    };
    // Swiper 8 needs an explicit loopedSlides count when slidesPerView is 'auto'.
    if (loop) options.loopedSlides = slideCount;

    if (config.arrows) {
      options.navigation = {
        nextEl: appendControl(container, 'swiper-button-next product-gallery-arrow'),
        prevEl: appendControl(container, 'swiper-button-prev product-gallery-arrow')
      };
    }
    if (config.dots) {
      options.pagination = {
        el: appendControl(container, 'swiper-pagination product-gallery-dots'),
        clickable: true
      };
    }
    if (thumbsSwiper) {
      options.thumbs = { swiper: thumbsSwiper };
    }
    return options;
  }

  function buildThumbsSwiper(container, config) {
    var direction = config.direction || 'horizontal';
    var options = {
      direction: direction,
      slidesPerView: 'auto',
      spaceBetween: typeof config.spaceBetween === 'number' ? config.spaceBetween : 10,
      freeMode: true,
      watchSlidesProgress: true, // required for the thumbs module (replaces v6/7 watchSlidesVisibility)
      watchOverflow: true,
      observer: true,
      observeParents: true,
      // RTL only affects the horizontal axis; it is a no-op (and a footgun) on a
      // vertical swiper, so we only pass the flag — the desktop vertical state ignores it.
      rtl: !!config.rtl,
      // Thumbs must NOT loop: clones (.swiper-slide-duplicate) would duplicate the
      // thumbnail <button> ids / aria-* (invalid HTML + breaks screen readers and
      // active-thumb sync). The thumbs module syncs by realIndex without looping.
      loop: false,
      // Thumbnails are already real <button>s with aria-label/controls/describedby —
      // Swiper's a11y module would double-label them.
      a11y: { enabled: false }
    };
    // "Thumbnail Side": horizontal strip on mobile, native vertical column at >=750px.
    // Swiper 8.4.7's setBreakpoint() calls changeDirection() when the breakpoint
    // direction differs, so this flips orientation responsively without a re-init.
    if (config.desktopDirection && config.desktopDirection !== direction) {
      options.breakpoints = {
        750: { direction: config.desktopDirection }
      };
    }
    return new Swiper(container, options);
  }

  // Wrap the slide elements inside a `.swiper-wrapper` (Swiper requires it).
  // Returns a teardown() that fully reverses the DOM changes.
  function wrapSlides(container, slideSelector) {
    var slides = Array.prototype.slice.call(
      container.querySelectorAll(':scope > ' + slideSelector)
    );
    var wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';
    slides.forEach(function (slide) {
      slide.classList.add('swiper-slide');
      wrapper.appendChild(slide);
    });
    container.insertBefore(wrapper, container.firstChild);
    container.classList.add('swiper');

    return function teardown() {
      // remove loop clones first
      Array.prototype.slice
        .call(container.querySelectorAll('.swiper-slide-duplicate'))
        .forEach(function (clone) { clone.remove(); });
      slides.forEach(function (slide) {
        slide.classList.remove(
          'swiper-slide',
          'swiper-slide-active',
          'swiper-slide-next',
          'swiper-slide-prev',
          'swiper-slide-visible'
        );
        slide.removeAttribute('style');
        container.appendChild(slide);
      });
      if (wrapper.parentNode) wrapper.remove();
      // remove any nav/pagination we appended
      Array.prototype.slice
        .call(container.querySelectorAll('.product-gallery-arrow, .product-gallery-dots'))
        .forEach(function (c) { c.remove(); });
      container.classList.remove('swiper', 'swiper-initialized');
      container.removeAttribute('style');
    };
  }

  function setRtl(container, config) {
    if (config.rtl) container.dir = 'rtl';
  }

  // STATIC galleries (thumbnail / thumbnail_slider): slider on every viewport.
  // The `.swiper-wrapper` is already rendered in Liquid.
  function initStatic(mainEl) {
    var config = parseConfig(mainEl, 'data-swiper-main');
    setRtl(mainEl, config);

    var thumbsSwiper = null;
    var thumbsEl = null;
    var thumbsConfig = null;
    if (config.thumbs) {
      thumbsEl = document.getElementById(
        mainEl.id.replace('Slider-Gallery', 'Slider-Thumbnails')
      );
      if (thumbsEl) {
        thumbsConfig = parseConfig(thumbsEl, 'data-swiper-thumbs');
        // NB: do not set dir="rtl" on the thumbs element — it can become a vertical
        // swiper (where RTL is a no-op and confuses translate math). RTL is passed as
        // an option instead and only affects the horizontal/mobile state.
        thumbsSwiper = buildThumbsSwiper(thumbsEl, thumbsConfig);
      }
    }

    var main = new Swiper(mainEl, buildMainOptions(mainEl, config, thumbsSwiper, countSlides(mainEl)));
    var record = { main: main, thumbs: thumbsSwiper, mode: 'static' };

    // "Thumbnail Side": the vertical thumb column has no height reference in the
    // flex row, so match it to the main image height (>=750px) and let Swiper
    // scroll the overflow. On mobile the strip is horizontal, so clear the height.
    if (thumbsSwiper && thumbsEl && thumbsConfig && thumbsConfig.desktopDirection === 'vertical') {
      var thumbsElRef = thumbsEl;
      record.syncHeight = function () {
        if (window.innerWidth < MOBILE_BREAKPOINT) {
          thumbsElRef.style.height = '';
        } else {
          thumbsElRef.style.height = mainEl.offsetHeight + 'px';
        }
        thumbsSwiper.update();
        // the column height just changed -> re-scroll the active thumb into view
        scrollThumbIntoView(main);
      };
      record.syncHeight();
      // autoHeight changes the main image height per slide; after each transition
      // settles, re-match the vertical thumb column to the new main height.
      main.on('transitionEnd', record.syncHeight);
      main.on('observerUpdate', record.syncHeight);
    }

    instances.push(record);
  }

  // MOBILE-ONLY galleries (columns / stacked): grid on desktop, slider <750px.
  function initMobile(mainEl) {
    var config = parseConfig(mainEl, 'data-swiper-main');
    var record = { main: null, thumbs: null, teardown: null, mode: 'mobile', el: mainEl, config: config };

    function build() {
      if (record.main) return;
      setRtl(mainEl, config);
      record.teardown = wrapSlides(mainEl, '.product__media-item');
      record.main = new Swiper(mainEl, buildMainOptions(mainEl, config, null, countSlides(mainEl)));
    }

    function destroy() {
      if (!record.main) return;
      record.main.destroy(true, false);
      record.main = null;
      if (record.teardown) record.teardown();
      record.teardown = null;
    }

    record.build = build;
    record.destroy = destroy;

    if (isMobile()) build();
    instances.push(record);
  }

  function handleResize() {
    instances.forEach(function (rec) {
      if (rec.mode === 'mobile') {
        if (isMobile()) rec.build();
        else rec.destroy();
      }
      if (rec.syncHeight) rec.syncHeight();
    });
  }

  function initAll() {
    var galleries = document.querySelectorAll('[data-swiper-main]');
    Array.prototype.forEach.call(galleries, function (mainEl) {
      if (mainEl.dataset.swiperBound === 'true') return;
      mainEl.dataset.swiperBound = 'true';
      var config = parseConfig(mainEl, 'data-swiper-main');
      if (config.mode === 'mobile') initMobile(mainEl);
      else initStatic(mainEl);
    });

    // Recalculate once everything (images) has loaded — image heights settle
    // here, which the vertical thumb-height sync depends on.
    window.addEventListener('load', function () {
      instances.forEach(function (rec) {
        if (rec.main && rec.main.updateAutoHeight) rec.main.updateAutoHeight();
        if (rec.syncHeight) rec.syncHeight();
        if (rec.main && rec.main.update) rec.main.update();
        if (rec.thumbs && rec.thumbs.update) rec.thumbs.update();
      });
    });
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(handleResize, 150);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Expose for variant-driven slide changes coming from global.js.
  window.ProductGallerySwiper = {
    instances: instances,
    refresh: handleResize
  };
})();
